// Phase 33 §2g · 20d — the rules behind the Live Call console.
//
// The queue is the part that is easy to get wrong. `handoff_pending` is the
// only status that means "a person is holding the line for a human" — the
// AI is still talking during `ai_conversation`, and `with_agent` is somebody
// else's call, not a wait. Ordered oldest-first, because the caller who has
// been holding longest is the one who should be answered next; the queue does
// NOT sort by how valuable the account is.

import type { VoiceSession } from '@services/voice.service';

/** mm:ss. A call is read in minutes and seconds, never as '4m'. */
export function callClock(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—';
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** How long this caller has been holding, in seconds. */
export function holdSeconds(s: VoiceSession, now = Date.now()): number | null {
  const t = new Date(s.createdAt).getTime();
  return Number.isNaN(t) ? null : Math.max(0, (now - t) / 1000);
}

/** Calls waiting for a human, longest hold first. */
export function queueRows(sessions: VoiceSession[], now = Date.now()): VoiceSession[] {
  return sessions
    .filter((s) => s.status === 'handoff_pending')
    .sort((a, b) => (holdSeconds(b, now) ?? 0) - (holdSeconds(a, now) ?? 0));
}

/** Where the caller is, when the carrier told us. Never a guess. */
export function callerPlace(s: VoiceSession): string | null {
  const parts = [s.callerCity, s.callerCountry].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0,
  );
  return parts.length ? parts.join(', ') : null;
}

/**
 * A caller we cannot match to an account. Worth saying out loud: answering
 * them costs exactly what answering a known customer costs.
 */
export function isUnmatched(s: VoiceSession): boolean {
  return !s.customerId;
}

/** The header line: what you are doing, and who is waiting on it. */
export function consoleSubtitle(
  opts: {
    onCall: boolean;
    callSeconds: number;
    queue: VoiceSession[];
    online: boolean;
  },
  now = Date.now(),
): string {
  const parts: string[] = [];
  parts.push(opts.onCall ? `On a call · ${callClock(opts.callSeconds)}` : opts.online ? 'Waiting for calls' : 'Not taking calls');

  if (opts.queue.length === 0) {
    parts.push('nobody in the queue');
  } else {
    const longest = holdSeconds(opts.queue[0], now);
    parts.push(
      `${opts.queue.length} in queue, longest holding ${callClock(longest)}`,
    );
  }
  return parts.join(' · ');
}
